import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  User,
  Mail,
  Shield,
  Building2,
  Save,
  ArrowLeft,
  Loader2,
  MapPin,
  AlertTriangle,
  Search
} from 'lucide-react'
import { usersService } from '@/services/users'
import { brandsService } from '@/services/brands'
import { areasService, userAreasService } from '@/services/areas'
import { ROUTES, USER_ROLES } from '@/utils/constants'
import { useAuth } from '@/context/AuthContext'
import { NavigationUtils } from '@/utils/navigation'
import toast from 'react-hot-toast'

const UserEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()
  const [selectedBrands, setSelectedBrands] = useState([])
  const [selectedAreas, setSelectedAreas] = useState([])
  const [areaConflicts, setAreaConflicts] = useState([])
  const [showConflictWarning, setShowConflictWarning] = useState(false)
  const [areaSearchTerm, setAreaSearchTerm] = useState('')

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm()

  const selectedRole = watch('role')

  // Fetch user data
  const {
    data: user,
    isLoading: userLoading,
    error: userError
  } = useQuery(
    ['user', id],
    () => usersService.getById(id),
    {
      onError: (error) => {
        toast.error('Failed to load user details')
        console.error('Error loading user:', error)
      }
    }
  )

  // Fetch brands for brand access selection
  const { data: brandsData = {} } = useQuery(
    'brands-for-user-edit',
    () => brandsService.getAll({ page_size: 1000, status: 'active' }),
    {
      onError: (error) => {
        console.error('Error loading brands:', error)
      }
    }
  )

  // Fetch areas for area access selection - filtered by selected brand
  const { data: areasData = {} } = useQuery(
    ['areas-for-user-edit', selectedBrands],
    () => {
      // If no brand is selected, don't fetch areas
      if (selectedBrands.length === 0) {
        return { data: [] }
      }
      // Fetch areas for the selected brand
      const brandId = selectedBrands[0] // Single brand selection
      return areasService.getAll({ page_size: 1000, is_active: true, brand_id: brandId })
    },
    {
      enabled: (selectedRole === USER_ROLES.AREA_MANAGER || selectedRole === USER_ROLES.USER) && selectedBrands.length > 0,
      onError: (error) => {
        console.error('Error loading areas:', error)
      }
    }
  )

  // Fetch user's current area assignments
  const { data: userAreasData = {} } = useQuery(
    ['user-areas', id],
    () => userAreasService.getUserAreas(id, { page_size: 1000 }),
    {
      enabled: !!id && (selectedRole === USER_ROLES.AREA_MANAGER || selectedRole === USER_ROLES.USER),
      onSuccess: (data) => {
        if (data.user_areas) {
          const userAreaIds = data.user_areas.map(ua => ua.area_id.toString())
          setSelectedAreas(userAreaIds)
        }
      },
      onError: (error) => {
        console.error('Error loading user areas:', error)
      }
    }
  )

  // Extract data from paginated responses
  const brands = brandsData.brands || []
  const areas = areasData.areas || []

  // Filter areas based on search term
  const filteredAreas = useMemo(() => {
    if (!areaSearchTerm.trim()) {
      return areas
    }
    const searchLower = areaSearchTerm.toLowerCase()
    return areas.filter(area =>
      area.name.toLowerCase().includes(searchLower) ||
      (area.brand_name && area.brand_name.toLowerCase().includes(searchLower))
    )
  }, [areas, areaSearchTerm])

  // Populate form when user data is available (from cache or server)
  useEffect(() => {
    if (user) {
      setValue('first_name', user.first_name)
      setValue('last_name', user.last_name)
      setValue('username', user.username)
      setValue('email', user.email)
      setValue('role', user.role)
      setValue('is_active', user.is_active)

      // Set selected brands
      if (user.brand_access) {
        const accessibleBrands = user.brand_access
          .filter(access => access.can_access)
          .map(access => access.brand_id.toString())
        setSelectedBrands(accessibleBrands)
      }
    }
  }, [user, setValue])

  // Update user mutation
  const updateUserMutation = useMutation(
    (userData) => usersService.update(id, userData),
    {
      onSuccess: () => {
        toast.success('User updated successfully!')
        // Invalidate all user-related queries for real-time updates
        queryClient.invalidateQueries(['user', id])
        queryClient.invalidateQueries(['users'])
        queryClient.invalidateQueries(['users', 'all'])
        navigate(`/users/${id}`) // Navigate to user detail page
      },
      onError: (error) => {
        toast.error('Failed to update user')
        console.error('Error updating user:', error)
      }
    }
  )

  const onSubmit = async (data) => {
    // Validate brand selection for non-admin roles
    if (data.role && data.role !== USER_ROLES.ADMIN && selectedBrands.length === 0) {
      toast.error('Brand selection is required for non-admin roles')
      return
    }

    // Area selection is now optional for area manager and user roles
    // No validation needed - users can be updated without area access

    const userData = {
      ...data,
      brand_access: selectedBrands.map(brandId => ({
        brand_id: parseInt(brandId),
        can_access: true
      })),
      area_access: selectedAreas.map(areaId => ({
        area_id: parseInt(areaId)
      }))
    }

    // Log the data being sent for debugging
    console.log('Frontend - Submitting user update with data:', userData)

    updateUserMutation.mutate(userData)
  }

  const handleBrandToggle = (brandId) => {
    setSelectedBrands(prev => {
      if (prev.includes(brandId)) {
        // If deselecting the current brand, clear areas as well
        setSelectedAreas([])
        return prev.filter(id => id !== brandId)
      } else {
        // Single brand selection - replace current selection and clear areas
        setSelectedAreas([])
        return [brandId]
      }
    })
  }

  const handleAreaToggle = (areaId) => {
    setSelectedAreas(prev => {
      const newAreas = prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]

      // Check for conflicts if user is area manager
      if (selectedRole === USER_ROLES.AREA_MANAGER && newAreas.length > 0) {
        checkAreaConflicts(newAreas)
      } else {
        setAreaConflicts([])
        setShowConflictWarning(false)
      }

      return newAreas
    })
  }

  const checkAreaConflicts = async (areaIds) => {
    try {
      const response = await usersService.checkAreaConflicts(areaIds, selectedRole)
      setAreaConflicts(response.conflicts || [])
      setShowConflictWarning(response.has_conflicts)
    } catch (error) {
      console.error('Error checking area conflicts:', error)
      setAreaConflicts([])
      setShowConflictWarning(false)
    }
  }

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading user details...</span>
        </div>
      </div>
    )
  }

  if (userError || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <User className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">User not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The user you're trying to edit doesn't exist or has been deleted.
          </p>
          <div className="mt-6">
            <button
              onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'users')}
              className="btn-primary btn-sm"
            >
              Back to Users
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/users/${id}`)}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to User
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Edit User: {user.first_name} {user.last_name}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Update user information and permissions
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Information */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow-sm rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">User Information</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Update basic user details
                </p>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                      First Name *
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        {...register('first_name', {
                          required: 'First name is required',
                          minLength: {
                            value: 2,
                            message: 'First name must be at least 2 characters',
                          },
                        })}
                        type="text"
                        className={`input pl-10 ${
                          errors.first_name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                        }`}
                        placeholder="Enter first name"
                      />
                    </div>
                    {errors.first_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                      Last Name *
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        {...register('last_name', {
                          required: 'Last name is required',
                          minLength: {
                            value: 2,
                            message: 'Last name must be at least 2 characters',
                          },
                        })}
                        type="text"
                        className={`input pl-10 ${
                          errors.last_name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                        }`}
                        placeholder="Enter last name"
                      />
                    </div>
                    {errors.last_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>
                    )}
                  </div>
                </div>

                {/* Username */}
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                    Username *
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register('username', {
                        required: 'Username is required',
                        minLength: {
                          value: 3,
                          message: 'Username must be at least 3 characters',
                        },
                        pattern: {
                          value: /^[a-zA-Z0-9_]+$/,
                          message: 'Username can only contain letters, numbers, and underscores',
                        },
                      })}
                      type="text"
                      className={`input pl-10 ${
                        errors.username ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                      }`}
                      placeholder="Enter username"
                    />
                  </div>
                  {errors.username && (
                    <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                      type="email"
                      className={`input pl-10 ${
                        errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                      }`}
                      placeholder="Enter email address"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Role and Permissions */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-sm rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Role & Permissions</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Update user role and brand access permissions
                </p>
              </div>
              <div className="p-6 space-y-6">
                {/* Role Selection */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    Role *
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Shield className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      {...register('role', {
                        required: 'Role is required',
                      })}
                      className={`input pl-10 ${
                        errors.role ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                      }`}
                    >
                      <option value="">Select a role</option>
                      {/* Admin can edit any role, Manager can only edit Manager/Area Manager/User */}
                      {currentUser?.role === USER_ROLES.ADMIN && (
                        <option value={USER_ROLES.ADMIN}>Admin</option>
                      )}
                      <option value={USER_ROLES.MANAGER}>Manager</option>
                      <option value={USER_ROLES.AREA_MANAGER}>Area Manager</option>
                      <option value={USER_ROLES.USER}>User</option>
                    </select>
                  </div>
                  {errors.role && (
                    <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                  )}

                  {/* Role Description */}
                  {selectedRole && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-md">
                      <p className="text-xs text-gray-600">
                        {selectedRole === USER_ROLES.ADMIN && 'Full system access including user management'}
                        {selectedRole === USER_ROLES.MANAGER && 'Can manage brands, areas, and data inputs within assigned brands'}
                        {selectedRole === USER_ROLES.AREA_MANAGER && 'Can manage records from users within assigned areas (no user management)'}
                        {selectedRole === USER_ROLES.USER && 'Can create and edit data within assigned brands and areas'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Active Status */}
                <div>
                  <label className="flex items-center">
                    <input
                      {...register('is_active')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active User</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Inactive users cannot log in to the system
                  </p>
                </div>

                {/* Brand Access */}
                {selectedRole && selectedRole !== USER_ROLES.ADMIN && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Brand Access *
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      Select one brand for this user. Areas will be filtered based on the selected brand.
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {brands.map((brand) => (
                        <label key={brand.id} className="flex items-center">
                          <input
                            type="radio"
                            name="brand-selection"
                            checked={selectedBrands.includes(brand.id.toString())}
                            onChange={() => handleBrandToggle(brand.id.toString())}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                          />
                          <div className="ml-3 flex items-center">
                            <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-700">{brand.name}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                    {selectedBrands.length === 0 && (
                      <p className="mt-2 text-xs text-red-600">
                        Please select a brand
                      </p>
                    )}
                  </div>
                )}

                {/* Area Access */}
                {(selectedRole === USER_ROLES.AREA_MANAGER || selectedRole === USER_ROLES.USER) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Area Access (Optional)
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      Select areas within the chosen brand. Area access is optional and can be assigned later.
                    </p>

                    {selectedBrands.length === 0 ? (
                      <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-sm text-gray-600">
                          Please select a brand first to see available areas.
                        </p>
                      </div>
                    ) : areas.length === 0 ? (
                      <div className="p-4 bg-yellow-50 rounded-md border border-yellow-200">
                        <p className="text-sm text-yellow-700">
                          No areas available for the selected brand.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Search input for areas */}
                        <div className="relative mb-3">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            placeholder="Search areas..."
                            value={areaSearchTerm}
                            onChange={(e) => setAreaSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        {/* Area list */}
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {filteredAreas.length > 0 ? (
                            filteredAreas.map((area) => (
                              <label key={area.id} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={selectedAreas.includes(area.id.toString())}
                                  onChange={() => handleAreaToggle(area.id.toString())}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <div className="ml-3 flex items-center">
                                  <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                                  <div>
                                    <span className="text-sm text-gray-700">{area.name}</span>
                                    {area.brand_name && (
                                      <span className="text-xs text-gray-500 ml-2">({area.brand_name})</span>
                                    )}
                                  </div>
                                </div>
                              </label>
                            ))
                          ) : (
                            <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                              <p className="text-sm text-gray-600">
                                No areas found matching "{areaSearchTerm}"
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {selectedAreas.length > 0 && (
                      <p className="mt-2 text-xs text-green-600">
                        {selectedAreas.length} area(s) selected
                      </p>
                    )}

                    {/* Area Manager Conflict Warning */}
                    {showConflictWarning && areaConflicts.length > 0 && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <AlertTriangle className="h-4 w-4 text-yellow-400" />
                          </div>
                          <div className="ml-3">
                            <h4 className="text-sm font-medium text-yellow-800">
                              Area Manager Conflicts Detected
                            </h4>
                            <div className="mt-2 text-sm text-yellow-700">
                              <p className="mb-2">
                                The following areas already have managers assigned. This user will be assigned as a regular user, not as the area manager:
                              </p>
                              <ul className="list-disc list-inside space-y-1">
                                {areaConflicts.map((conflict) => (
                                  <li key={conflict.area_id}>
                                    <strong>{conflict.area_name}</strong> - Currently managed by {conflict.current_manager_name} ({conflict.current_manager_email})
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/users/${id}`)}
            className="btn-outline btn-md"
            disabled={updateUserMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary btn-md flex items-center"
            disabled={updateUserMutation.isLoading}
          >
            {updateUserMutation.isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Updating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Update User
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default UserEdit
